import { useCallback, useEffect, useRef } from "react";
import { APP_VERSION_LABEL } from "../../config/appVersion";

const BRAND_LAUNCH_ID = "chekhabare-brand-launch";
const BRAND_LAUNCH_VERSION_ID = "chekhabare-brand-launch__version";
const BRAND_LAUNCH_EXIT_CLASS = "chekhabare-brand-launch--exiting";

const MINIMUM_BRAND_VISIBLE_MS = 780;
const EXIT_TRANSITION_FALLBACK_MS = 260;

const AppLaunchScreen = () => {
  const isCompletedRef = useRef(false);

  const completeLaunchHandoff = useCallback(() => {
    if (isCompletedRef.current) {
      return;
    }

    isCompletedRef.current = true;
    window.__CHEKHABARE_LAUNCH_SCREEN_HIDDEN__ = true;
    window.dispatchEvent(
      new CustomEvent("chekhabare:launch-screen-hidden")
    );
  }, []);

  useEffect(() => {
    const launchElement = document.getElementById(BRAND_LAUNCH_ID);
    const versionElement = document.getElementById(BRAND_LAUNCH_VERSION_ID);

    // Keep the pre-React version label synchronized even in development
    // sessions where index.html was not rebuilt yet.
    if (versionElement) {
      versionElement.textContent = APP_VERSION_LABEL;
    }

    if (!launchElement) {
      completeLaunchHandoff();
      return undefined;
    }

    const appEntryAt =
      Number(window.__CHEKHABARE_APP_ENTRY_AT__) || Date.now();

    let releaseTimerId = null;
    let exitFallbackTimerId = null;
    let firstPaintFrameId = null;
    let secondPaintFrameId = null;
    let hasStartedExit = false;

    const removeLaunchElement = () => {
      if (exitFallbackTimerId !== null) {
        window.clearTimeout(exitFallbackTimerId);
        exitFallbackTimerId = null;
      }

      launchElement.removeEventListener(
        "transitionend",
        handleTransitionEnd
      );

      if (launchElement.isConnected) {
        launchElement.remove();
      }

      completeLaunchHandoff();
    };

    const handleTransitionEnd = (event) => {
      if (
        event.target !== launchElement ||
        event.propertyName !== "opacity"
      ) {
        return;
      }

      removeLaunchElement();
    };

    const startExit = () => {
      if (hasStartedExit) {
        return;
      }

      hasStartedExit = true;
      launchElement.addEventListener(
        "transitionend",
        handleTransitionEnd
      );
      launchElement.classList.add(BRAND_LAUNCH_EXIT_CLASS);

      // transitionend can be skipped by browsers in edge cases such as page
      // visibility changes. Never let that keep the launch overlay mounted.
      exitFallbackTimerId = window.setTimeout(
        removeLaunchElement,
        EXIT_TRANSITION_FALLBACK_MS
      );
    };

    const scheduleExitAfterMinimumVisibility = () => {
      const minimumReleaseAt =
        appEntryAt + MINIMUM_BRAND_VISIBLE_MS;
      const remainingMs = Math.max(0, minimumReleaseAt - Date.now());

      releaseTimerId = window.setTimeout(startExit, remainingMs);
    };

    // AppLaunchScreen is mounted in the same React commit as the application.
    // Two frames guarantee that the first React/Skeleton paint can exist behind
    // the pre-React launch before the overlay starts fading away.
    firstPaintFrameId = window.requestAnimationFrame(() => {
      secondPaintFrameId = window.requestAnimationFrame(
        scheduleExitAfterMinimumVisibility
      );
    });

    return () => {
      if (firstPaintFrameId !== null) {
        window.cancelAnimationFrame(firstPaintFrameId);
      }

      if (secondPaintFrameId !== null) {
        window.cancelAnimationFrame(secondPaintFrameId);
      }

      if (releaseTimerId !== null) {
        window.clearTimeout(releaseTimerId);
      }

      if (exitFallbackTimerId !== null) {
        window.clearTimeout(exitFallbackTimerId);
      }

      launchElement.removeEventListener(
        "transitionend",
        handleTransitionEnd
      );
    };
  }, [completeLaunchHandoff]);

  return null;
};

export default AppLaunchScreen;
